import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Form, Input, Button, Typography, Flex, Alert, message, Card, Avatar, Space } from 'antd'
import { EyeInvisibleOutlined, EyeTwoTone, ClearOutlined, ToolOutlined } from '@ant-design/icons'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import ThemeToggle from '../components/ThemeToggle'
import AppIcon from '../components/AppIcon'
import '../styles/global-input-fixes.css'
import { isAndroid, openConfig } from '../utils/androidUtils'


const { Title, Text } = Typography

// Chave usada para persistir o código do usuário
const USER_CODE_STORAGE_KEY = 'login.codigo'

// Função para traduzir mensagens de erro para português
const translateErrorMessage = (errorMessage) => {
  const errorTranslations = {
    'Invalid credentials': 'Email ou senha incorretos. Verifique suas credenciais e tente novamente.',
    'User already exists': 'Este email já está cadastrado no sistema.',
    'Authentication failed': 'Falha na autenticação. Faça login novamente.',
    'Usuário inativo. Entre em contato com o administrador.': 'Sua conta está inativa. Entre em contato com o administrador para reativar seu acesso.',
    'Usuário não encontrado ou inativo': 'Sua conta está inativa ou não foi encontrada. Entre em contato com o administrador.',
    'Network Error': 'Erro de conexío. Verifique sua internet e tente novamente.',
    'HTTP error! status: 500': 'Erro interno do servidor. Tente novamente em alguns minutos.',
    'HTTP error! status: 404': 'Serviço não encontrado. Verifique se o servidor está funcionando.',
    'HTTP error! status: 400': 'Dados inválidos. Verifique as informações inseridas.',
    'Failed to fetch': 'Não foi possível conectar ao servidor. Verifique sua conexío com a internet.'
  }

  // Verifica se existe uma tradução específica
  if (errorTranslations[errorMessage]) {
    return errorTranslations[errorMessage]
  }

  // Verifica se a mensagem contém alguma das chaves conhecidas
  for (const [key, translation] of Object.entries(errorTranslations)) {
    if (errorMessage.includes(key)) {
      return translation
    }
  }

  // Se não encontrar tradução, retorna uma mensagem genérica amigável
  return 'Ocorreu um erro inesperado. Tente novamente ou entre em contato com o suporte.'
}

const Login = () => {
  const [form] = Form.useForm()
  const [clientImage, setClientImage] = useState(null)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [clientImageSize, setClientImageSize] = useState(null)
  const [formLogoSize, setFormLogoSize] = useState(null)
  const [formLogoLoaded, setFormLogoLoaded] = useState(false)
  const { login, isLoading, error, clearError } = useAuth()
  const { currentTheme } = useTheme()
  const isDark = currentTheme === 'dark'
  const navigate = useNavigate()
  const location = useLocation()

  // Função para limpar cache
  const clearCache = () => {
    // Limpar localStorage
    localStorage.clear();

    // Limpar sessionStorage
    sessionStorage.clear();

    // Limpar cookies (apenas os que podem ser acessados via JavaScript)
    document.cookie.split(";").forEach(function (c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    // Tentar limpar cache do service worker se disponível
    if ('caches' in window) {
      caches.keys().then(function (names) {
        for (let name of names) {
          caches.delete(name);
        }
      });
    }

    alert('Cache limpo com sucesso! A página será recarregada.');
    window.location.reload();
  };

  // Responsive styles
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)

  // Restaurar o código salvo ao abrir a página
  useEffect(() => {
    try {
      const savedCodigo = localStorage.getItem(USER_CODE_STORAGE_KEY)
      if (savedCodigo) {
        form.setFieldsValue({ codigo: savedCodigo })
      }
    } catch (_) {
      // Ignora erros de acesso ao localStorage
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Clear any previous errors when component mounts
  useEffect(() => {
    clearError()
  }, [])

  // Check for custom client image
  useEffect(() => {
    const checkClientImage = async () => {
      const imagePath = import.meta.env.VITE_CLIENT_LOGIN_IMAGE

      if (imagePath) {
        try {
          // Create a new image to test if it loads
          const img = new Image()
          img.onload = () => {
            setClientImage(imagePath)
            setImageLoaded(true)
            // Captura dimensões naturais para cálculo de proporção
            setClientImageSize({ width: img.naturalWidth, height: img.naturalHeight })
          }
          img.onerror = () => {
            console.warn('Client image not found or failed to load:', imagePath)
            setImageLoaded(false)
          }
          img.src = imagePath
        } catch (error) {
          console.warn('Error loading client image:', error)
          setImageLoaded(false)
        }
      }
    }

    checkClientImage()
  }, [])

  // Carregar e medir a imagem /cliente.png para exibir acima do formulário
  useEffect(() => {
    const logoPath = '/cliente.png'
    try {
      const img = new Image()
      img.onload = () => {
        setFormLogoLoaded(true)
        setFormLogoSize({ width: img.naturalWidth, height: img.naturalHeight })
      }
      img.onerror = () => {
        setFormLogoLoaded(false)
      }
      img.src = logoPath
    } catch (_) {
      setFormLogoLoaded(false)
    }
  }, [])

  const handleSubmit = async (values) => {
    try {
      // Remove caracteres . e - do campo código antes de enviar
      const cleanedValues = {
        ...values,
        codigo: values.codigo ? values.codigo.replace(/[.-]/g, '') : values.codigo
      }

      const result = await login(cleanedValues)

      if (result.success) {
        // O redirecionamento será feito automaticamente pelo PublicRoute baseado no role do usuário
        // Não fazemos redirecionamento manual aqui
      } else {
        // O erro já foi capturado pelo contexto de autenticação
        // e será exibido automaticamente pelo componente Alert
        console.error('Login failed:', result.error)
        // Alert personalizado para informar o erro ao usuário
        message.error({
          content: translateErrorMessage(result.error),
          duration: 5,
          style: {
            marginTop: '20vh',
          },
        })
      }
    } catch (error) {
      // Captura erros não tratados pelo contexto
      console.error('Unexpected login error:', error)
    }
  }

  const handleFieldChange = (changedFields, allFields) => {
    // Limpa erro quando o usuário começa a digitar
    if (error) {
      clearError()
    }

    // Persiste o valor do campo "Código" no localStorage
    try {
      const codigoField = (changedFields || []).find(
        (f) => Array.isArray(f.name) && f.name[0] === 'codigo'
      ) || (allFields || []).find(
        (f) => Array.isArray(f.name) && f.name[0] === 'codigo'
      )

      if (codigoField) {
        const value = codigoField.value ?? ''
        localStorage.setItem(USER_CODE_STORAGE_KEY, value)
      }
    } catch (_) {
      // Ignora erros de acesso ao localStorage
    }
  }

  return (
    <div
      className="login-container"
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        background: isDark ? '#000' : '#f5f5f5',
        margin: 0,
        padding: 0,
        overflow: 'hidden'
      }}
    >
      {/* Botío de ferramenta para Android - posicionado no topo direito */}
      {isAndroid() && (
        <Button
          type="text"
          icon={<ToolOutlined />}
          onClick={openConfig}
          style={{
            position: 'absolute',
            top: '16px',
            right: '64px',
            zIndex: 1000,
            color: isDark ? '#fff' : '#666',
            border: 'none',
            backgroundColor: isDark ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
          title="Ferramentas"
        />
      )}

      {/* Left Side - Image Section */}
      {isMobile ? (
        <div
          className="login-left-section-mobile"
          style={{
            background: isDark
              ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
              : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            minHeight: '30vh',
            flex: isMobile ? '0 0 auto' : 'none'
          }}
        >
          {/* Imagem do cliente renderizada com proporções mínimas de 500px */}
          {clientImage && imageLoaded && clientImageSize && (
            <img
              src={clientImage}
              alt="Imagem do Cliente"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                objectFit: 'contain',
                zIndex: 0,
                // Regras de tamanho: paisagem -> height 500, largura automática; retrato -> width 500; quadrada -> 500x500
                ...(clientImageSize.width > clientImageSize.height
                  ? { height: '500px', width: 'auto' }
                  : clientImageSize.height > clientImageSize.width
                    ? { width: '500px', height: 'auto' }
                    : { width: '500px', height: '500px' }),
                // Evitar overflow em telas menores
                maxWidth: '95vw',
                maxHeight: '70vh',
                borderRadius: '12px'
              }}
            />
          )}
          <div
            className="login-left-content"
            style={{
              textAlign: 'center',
              color: 'white',
              zIndex: 1,
              padding: 'clamp(20px, 5vw, 40px)'
            }}
          >
            <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <AppIcon
                size={isMobile ? 64 : 48}
                style={{
                  display: 'block',
                  minWidth: isMobile ? '64px' : '48px',
                  minHeight: isMobile ? '64px' : '48px',
                  maxWidth: isMobile ? '80px' : '60px',
                  maxHeight: isMobile ? '80px' : '60px',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                }}
              />
            </div>
            <Title level={2} style={{ color: 'white', marginBottom: '8px', fontSize: 'clamp(24px, 5vw, 32px)' }}>EtiquetandoApp</Title>


          </div>
        </div>
      ) : (
        <div
          className="login-left-section"
          style={{
            flex: 1,
            background: isDark
              ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
              : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            minHeight: '100vh'
          }}
        >
          {/* Imagem do cliente renderizada com proporções mínimas de 500px */}
          {clientImage && imageLoaded && clientImageSize && (
            <img
              src={clientImage}
              alt="Imagem do Cliente"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                objectFit: 'contain',
                zIndex: 0,
                ...(clientImageSize.width > clientImageSize.height
                  ? { height: '500px', width: 'auto' }
                  : clientImageSize.height > clientImageSize.width
                    ? { width: '500px', height: 'auto' }
                    : { width: '500px', height: '500px' }),
                maxWidth: '80vw',
                maxHeight: '80vh',
                borderRadius: '12px'
              }}
            />
          )}
          <div
            className="login-left-content"
            style={{
              textAlign: 'center',
              color: 'white',
              zIndex: 1,
              padding: 'clamp(20px, 5vw, 40px)'
            }}
          >
            <div style={{ marginBottom: '30px' }}>
              <AppIcon size={80} />
            </div>
            <Title level={1} style={{ color: 'white', marginBottom: '16px', fontSize: 'clamp(32px, 6vw, 48px)' }}>EtiquetandoApp</Title>

            <br />
            <br />
            <Text style={{ fontSize: 'clamp(14px, 2.5vw, 16px)', color: 'rgba(255, 255, 255, 0.8)' }}>
            </Text>
          </div>
        </div>
      )}

      {/* Right Side - Login Form */}
      <div
        className="login-right-section"
        style={{
          flex: isMobile ? 'none' : 1,
          width: isMobile ? '100%' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '20px' : '40px',
          overflowY: 'auto',
          background: isDark ? '#111111' : 'white',
          minHeight: isMobile ? '70vh' : '100vh',
          position: 'relative'
        }}
      >
        <div style={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: isDark ? '#1a1a1a' : 'transparent',
          padding: isDark ? '30px' : '0',
          borderRadius: '12px',
          boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.5)' : 'none',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isMobile ? 'flex-start' : 'center'
        }}>
          {/* Logo do cliente acima do formulário */}
          {formLogoLoaded && formLogoSize && (
            <div style={{
              textAlign: 'center',
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <img
                src="/cliente.png"
                alt="Logo Cliente"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  maxHeight: '80px',
                  objectFit: 'contain',
                  borderRadius: '12px'
                }}
              />
            </div>
          )}

          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <Title level={2} style={{ margin: 0, color: isDark ? '#fff' : '#1a1a1a' }}>Bem-vindo</Title>
            <Text type="secondary" style={{ color: isDark ? '#aaa' : undefined }}>Faça login para continuar</Text>
          </div>

          {/* Client Logo Area removida: logo renderizado acima do formulário */}

          {/* Error Display */}
          {error && (
            <>
              <Alert
                message="Erro de Login"
                description={translateErrorMessage(error)}
                type="error"
                showIcon
                style={{ marginBottom: '24px', borderRadius: '8px' }}
              />
            </>
          )}




          {/* Login Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onFieldsChange={handleFieldChange}
            autoComplete="off"
          >
            {/* Código Field */}
            <Form.Item
              label={<span style={{ color: isDark ? '#fff' : '#333', fontWeight: '500' }}>Código</span>}
              name="codigo"
              rules={[
                {
                  required: true,
                  message: 'Por favor, insira seu código!',
                },
              ]}
              style={{ marginBottom: isMobile ? '12px' : '16px' }}
            >
              <Input
                placeholder="Digite seu código"
                autoComplete="codigo"
                style={{
                  height: isMobile ? '35px' : '45px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  border: isDark ? '1px solid #444' : '1px solid #d9d9d9',
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: isDark ? '#1e293b' : '#fff',
                  color: isDark ? '#ffffff' : '#000000',
                  WebkitTextFillColor: isDark ? '#ffffff' : '#000000'
                }}
              />
            </Form.Item>

            {/* Password Field */}
            <Form.Item
              label={<span style={{ color: isDark ? '#fff' : '#333', fontWeight: '500' }}>Senha</span>}
              name="password"
              rules={[
                {
                  required: true,
                  message: 'Por favor, insira sua senha!',
                },
              ]} style={{ marginBottom: isMobile ? '12px' : '16px' }}
            >
              <Input.Password
                placeholder="••••••••••"
                autoComplete="current-password"
                style={{
                  height: isMobile ? '40px' : '45px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  border: isDark ? '1px solid #444' : '1px solid #d9d9d9',
                  backgroundColor: isDark ? '#1e293b' : '#fff',
                  color: isDark ? '#fff' : '#000',
                  background: 'inherit'
                }}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>


            {/* Submit Button */}
            <Form.Item style={{ marginBottom: isMobile ? '8px' : '12px' }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isLoading}
                style={{
                  height: isMobile ? '40px' : '45px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                Entrar
              </Button>
            </Form.Item>
          </Form>

          {/* Sign Up Link */}
          <Flex justify="center">
            <Button
              type="text"
              style={{
                border: 'none',
                height: 'auto',
                padding: '8px 0'
              }}
            >

            </Button>
          </Flex>

          {/* Clear Cache Button - Discreto no footer */}
          <Flex justify="center" style={{ marginTop: '8px' }}>
            <Button
              type="text"
              icon={<ClearOutlined />}
              onClick={clearCache}
              size="small"
              style={{
                color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                border: 'none',
                fontSize: '12px',
                height: 'auto',
                padding: '4px 8px'
              }}
              title="Limpar Cache"
            >
              Limpar cache
            </Button>
          </Flex>
        </div>


      </div>

      {/* Botío de alternar tema */}
      <ThemeToggle
        style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          zIndex: 1000,
          color: isDark ? '#fff' : '#666',
          backgroundColor: isDark ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      />


    </div>
  )
}

export default Login